// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 3 — The map, in two modes.
//
// REPLAY steps an episode forward hour by hour across every cluster that saw it, with wind
// vectors drawn from the same mast in the same hour.
// LIVE shows the current hour, fetched client-side from the two CORS-clean feeds.
//
// Leaflet notes that are not optional:
//   * `zoomAnimation: false` — an interrupted zoom welds a `scale()` onto the tile container and
//     the map renders exactly one level too far out, silently, with getZoom() still truthful.
//   * fitBounds must not run on a zero-height container; on a 0×0 box it returns MAXIMUM zoom
//     and renders one street corner.
//   * The initial view is set SYNCHRONOUSLY as well as in an animation frame, because
//     requestAnimationFrame never fires in a background tab — a map whose view is only set there
//     shows no tiles, no layers and no error to anyone who opened the page in one.
//   * The container establishes its own stacking context in CSS (`isolation: isolate`), or
//     Leaflet's panes escape to the page root and paint over every modal on the site.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Bundle } from '../data';
import { loadEpisodeSlice } from '../data';
import type { Episode, EpisodeSlice, LiveReading } from '../types';
import { el, emptyState, legend, panel, select } from '../components/ui';
import { concentration, number, prettyHour, UNIT } from '../format';
import { ABOVE_SCALE, bandColour, bandLabel, BANDS, HOUR_BOUNDS, WIND } from '../palette';
import { fetchLive, relativeTime } from '../live';
import { getState, setState } from '../state';
import { openStation } from './stationDetail';
import { glossaryLink } from '../glossary';

const AU_CENTRE: [number, number] = [-27.5, 148];

function makeMap(canvas: HTMLElement): L.Map {
  const map = L.map(canvas, {
    minZoom: 3,
    maxZoom: 12,
    zoomControl: true,
    scrollWheelZoom: false,
    zoomAnimation: false,
    attributionControl: true,
  });
  map.attributionControl.setPrefix(false);
  // Set a view synchronously so the map is never blank, even in a background tab.
  map.setView(AU_CENTRE, 4);
  // The basemap follows the reader's theme. A dark basemap under a light page (or the reverse)
  // reads as a broken image rather than as a design choice.
  const dark =
    document.documentElement.dataset.theme === 'dark' ||
    (document.documentElement.dataset.theme !== 'light' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches !== false);
  L.tileLayer(`https://{s}.basemaps.cartocdn.com/${dark ? 'dark_nolabels' : 'light_nolabels'}/{z}/{x}/{y}{r}.png`, {
    attribution: 'Tiles © CARTO · Monitoring data © NSW DCCEEW, Qld DETSI and the ACT Government (CC BY 4.0)',
    subdomains: 'abcd',
    minZoom: 3,
    maxZoom: 12,
  }).addTo(map);
  return map;
}

function fitWhenLaid(map: L.Map, canvas: HTMLElement, bounds: L.LatLngBounds): void {
  const fit = () => {
    map.invalidateSize();
    if (bounds.isValid() && canvas.clientHeight > 50) map.fitBounds(bounds, { padding: [24, 24] });
  };
  const observer = new ResizeObserver(() => {
    if (canvas.clientHeight > 50) {
      fit();
      observer.disconnect();
    }
  });
  observer.observe(canvas);
  // Both an animation frame and a timeout: rAF never fires in a background tab.
  requestAnimationFrame(fit);
  setTimeout(fit, 400);
}

function radiusFor(value: number | null): number {
  if (value === null) return 4;
  return Math.max(5, Math.min(34, 4 + Math.sqrt(Math.max(value, 0)) * 1.5));
}

function windVector(map: L.Map, lat: number, lon: number, degrees: number, speed: number): L.Polyline {
  // Meteorological convention: direction is where the wind comes FROM, so the arrow points the
  // way the air is going. Drawn in cold blue, outside the concentration ramp entirely, so it can
  // never be misread as a value.
  const length = Math.min(0.32, 0.05 + speed * 0.022);
  const toward = ((degrees + 180) % 360) * (Math.PI / 180);
  const end: [number, number] = [lat + Math.cos(toward) * length, lon + (Math.sin(toward) * length) / Math.cos((lat * Math.PI) / 180)];
  // Non-interactive on purpose. A Leaflet polyline is clickable by default, and a wind vector
  // drawn from a station's centre lies directly over that station's marker — so it silently
  // swallows the click that was meant to open the station, and shows its own tooltip instead of
  // the reading. The wind is already named in the marker's tooltip; it needs no second one.
  const line = L.polyline([[lat, lon], end], {
    color: WIND,
    weight: 1.6,
    opacity: 0.85,
    interactive: false,
  });
  void map;
  return line;
}

// ── Replay ──────────────────────────────────────────────────────────────────

function renderReplay(
  container: HTMLElement,
  bundle: Bundle,
  episode: Episode,
  slice: EpisodeSlice
): void {
  const frame = el('div', 'map-frame');
  const canvas = el('div', 'map-canvas');
  frame.append(canvas);
  container.append(frame);

  const map = makeMap(canvas);
  const layer = L.layerGroup().addTo(map);
  const windLayer = L.layerGroup().addTo(map);

  const bounds = L.latLngBounds(slice.rows.map((row) => [row.lat, row.lon] as [number, number]));
  fitWhenLaid(map, canvas, bounds);

  const hours = slice.to - slice.from + 1;
  let cursor = episode.peakHour - slice.from;
  let timer: number | null = null;

  const stamp = el('span', 'scrubber-stamp');
  const scrubber = el('div', 'scrubber');
  const play = el('button', 'button');
  play.type = 'button';
  const range = el('input') as HTMLInputElement;
  range.type = 'range';
  range.min = '0';
  range.max = String(hours - 1);
  range.step = '1';
  range.setAttribute('aria-label', 'Hour of the episode');

  const detail = el('div');

  function localStamp(index: number): string {
    // The slice is keyed on absolute UTC hours; every jurisdiction here is a fixed +10 offset,
    // so the local clock is a straight addition with no daylight-saving case to get wrong.
    const utcHour = slice.from + index;
    const date = new Date((utcHour + 10) * 3_600_000);
    return `${date.toISOString().slice(0, 10)}T${String(date.getUTCHours()).padStart(2, '0')}:00`;
  }

  function draw(): void {
    layer.clearLayers();
    windLayer.clearLayers();
    const utcHour = slice.from + cursor;
    let worst: { row: (typeof slice.rows)[number]; value: number } | null = null;

    for (const row of slice.rows) {
      const cell = row.values[cursor];
      const pm25 = cell?.[0] ?? null;
      const pm10 = cell?.[1] ?? null;
      const direction = cell?.[2] ?? null;
      const speed = cell?.[3] ?? null;
      const elevated = pm25 !== null && pm25 > bundle.meta.smoke1.absoluteFloorUgM3;
      const aboveScale = pm25 !== null && pm25 > HOUR_BOUNDS['pm2.5'][HOUR_BOUNDS['pm2.5'].length - 1];

      const marker = L.circleMarker([row.lat, row.lon], {
        radius: radiusFor(pm25),
        // Missing data is a hollow cross-hatched ring, never a faded fill — on this map a fill
        // fading toward the ground means clean air.
        fillColor: pm25 === null ? 'transparent' : aboveScale ? ABOVE_SCALE : bandColour(pm25),
        fillOpacity: pm25 === null ? 0 : 0.82,
        color: pm25 === null ? 'var(--missing)' : aboveScale ? '#FFFFFF' : 'rgba(0,0,0,0.45)',
        weight: pm25 === null ? 1.2 : aboveScale ? 1.6 : 0.8,
        dashArray: pm25 === null ? '3 2' : undefined,
      });
      marker.bindTooltip(
        `${row.name} (${row.jurisdiction})\n${prettyHour(localStamp(cursor))}\n` +
          `PM2.5 ${concentration(pm25)} ${UNIT}${pm25 === null ? '' : ` — ${bandLabel(pm25)}`}\n` +
          `PM10 ${concentration(pm10)} ${UNIT}\n` +
          (direction !== null && speed !== null ? `Wind ${Math.round(direction)}° at ${speed.toFixed(1)} m/s` : 'No wind measured here') +
          `\n${row.km} km from where this episode was first seen`,
        { sticky: true, className: 'map-tip' }
      );
      marker.on('click', () => {
        const station = bundle.stations.find((s) => s.id === row.clusterId);
        if (station) void openStation(station);
      });
      marker.addTo(layer);

      if (direction !== null && speed !== null && speed > 0.3) {
        windVector(map, row.lat, row.lon, direction, speed).addTo(windLayer);
      }
      if (pm25 !== null && (!worst || pm25 > worst.value)) worst = { row, value: pm25 };
      if (elevated) marker.setStyle({ weight: 1.6, color: '#FFFFFF' });
    }

    stamp.textContent = prettyHour(localStamp(cursor));
    range.value = String(cursor);
    detail.innerHTML = worst
      ? `<strong>${worst.row.name}</strong> is the dirtiest cluster this hour at <strong>${concentration(
          worst.value
        )} ${UNIT}</strong> — ${bandLabel(worst.value)}. ${slice.rows.length} clusters are drawn; ${
          slice.rows.filter((row) => row.values[cursor]?.[0] !== null && row.values[cursor]?.[0] !== undefined).length
        } reported a reading in this hour.`
      : 'No cluster reported a reading in this hour. The gaps are drawn as hollow rings rather than left off the map.';
    void utcHour;
  }

  function stop(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    play.textContent = '▶ Play';
    play.classList.remove('button-on');
  }

  play.addEventListener('click', () => {
    if (timer !== null) {
      stop();
      return;
    }
    play.textContent = '❚❚ Pause';
    play.classList.add('button-on');
    // The longest episode in the archive runs over sixty days. Stepping it one hour at a time
    // would take seven minutes to play through, so long episodes advance in larger steps and a
    // reader who wants every hour uses the scrubber.
    const step = Math.max(1, Math.round(hours / 240));
    timer = window.setInterval(() => {
      cursor = (cursor + step) % hours;
      draw();
    }, 260);
  });
  range.addEventListener('input', () => {
    stop();
    cursor = Number(range.value);
    draw();
  });

  const jump = el('button', 'button');
  jump.type = 'button';
  jump.textContent = 'Jump to peak hour';
  jump.addEventListener('click', () => {
    stop();
    cursor = episode.peakHour - slice.from;
    draw();
  });

  scrubber.append(play, range, stamp, jump);
  container.append(scrubber);
  container.append(el('div', 'note', ''));
  (container.lastChild as HTMLElement).append(detail);

  container.append(
    legend([
      ...BANDS.map((band, index) => ({
        label: band.label,
        hex: band.hex,
        tip:
          index === 0
            ? `PM2.5 up to ${HOUR_BOUNDS['pm2.5'][0]} ${UNIT}`
            : index < HOUR_BOUNDS['pm2.5'].length
              ? `${HOUR_BOUNDS['pm2.5'][index - 1]}–${HOUR_BOUNDS['pm2.5'][index]} ${UNIT}`
              : `above ${HOUR_BOUNDS['pm2.5'][HOUR_BOUNDS['pm2.5'].length - 1]} ${UNIT}`,
      })),
      { label: 'above the scale', hex: ABOVE_SCALE, tip: 'Higher than the published category scale reaches' },
      { label: 'wind', hex: WIND, tip: 'Points the way the air is moving' },
      { label: 'no reading', hex: 'var(--missing)', tip: 'Drawn as a hollow dashed ring, never as clean air' },
    ])
  );

  draw();
  // Stop the timer when the view is replaced, or it keeps ticking against a detached map.
  const observer = new MutationObserver(() => {
    if (!document.body.contains(canvas)) {
      stop();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// ── Live ────────────────────────────────────────────────────────────────────

function renderLive(container: HTMLElement, bundle: Bundle): void {
  const frame = el('div', 'map-frame');
  const canvas = el('div', 'map-canvas');
  frame.append(canvas);
  container.append(frame);

  const status = el('div', 'map-legend-row');
  container.append(status);
  const detail = el('p', 'note', 'Reading the live feeds…');
  container.append(detail);

  const map = makeMap(canvas);
  // Two layers, not one with a sentinel: the NSW markers persist across every refresh while the
  // live ones are replaced, and telling them apart by a style property would break the moment
  // that style changed.
  const staleLayer = L.layerGroup().addTo(map);
  const layer = L.layerGroup().addTo(map);
  const controller = new AbortController();

  // NSW cannot be live: its API sends no access-control header and its preflight 404s, so no
  // browser can reach it. Its stations are drawn from the last build, hollow, and labelled.
  for (const station of bundle.stations.filter((s) => s.jurisdiction === 'NSW')) {
    const marker = L.circleMarker([station.lat, station.lon], {
      radius: 4,
      fillOpacity: 0,
      color: 'var(--missing)',
      weight: 1,
      dashArray: '2 2',
    });
    marker.bindTooltip(
      `${station.name} (NSW)\nNo live reading available.\nNew South Wales does not allow a browser to read its air quality API directly, so this station only updates when this site rebuilds.`,
      { sticky: true, className: 'map-tip' }
    );
    marker.addTo(staleLayer);
  }

  async function refresh(): Promise<void> {
    try {
      const result = await fetchLive(bundle.stations, controller.signal);
      layer.clearLayers();

      let worst: LiveReading | null = null;
      for (const reading of result.readings) {
        const marker = L.circleMarker([reading.lat, reading.lon], {
          radius: radiusFor(reading.pm25),
          fillColor: bandColour(reading.pm25),
          fillOpacity: 0.78,
          // Every live reading is provisional, so every one is drawn dashed. It can never be
          // mistaken for the validated archive.
          color: '#FFFFFF',
          weight: 1,
          dashArray: '3 2',
        });
        marker.bindTooltip(
          `${reading.stationName} (${reading.jurisdiction})\n` +
            `PM2.5 ${concentration(reading.pm25)} ${UNIT}${reading.pm25 === null ? '' : ` — ${bandLabel(reading.pm25)}`}\n` +
            `PM10 ${concentration(reading.pm10)} ${UNIT}\n` +
            `${reading.lowCost ? 'Low-cost smoke sensor, not a reference monitor\n' : ''}` +
            'Provisional — about one in five recent readings is later revised',
          { sticky: true, className: 'map-tip' }
        );
        marker.on('click', () => {
          const station = reading.clusterId
            ? bundle.stations.find((s) => s.id === reading.clusterId)
            : undefined;
          if (station) void openStation(station);
        });
        marker.addTo(layer);
        if (reading.pm25 !== null && (!worst || (worst.pm25 ?? 0) < reading.pm25)) worst = reading;
      }

      status.innerHTML = '';
      const chip = el('span', 'freshness');
      chip.textContent = result.newest
        ? `Queensland and the ACT, ${relativeTime(result.newest)}`
        : 'Live feeds returned no current reading';
      status.append(chip);
      const nswChip = el('span', 'freshness');
      nswChip.textContent = `New South Wales — as at the last build`;
      nswChip.setAttribute(
        'data-tip',
        'The NSW API cannot be read from a browser: it sends no cross-origin header and its preflight request 404s. Its readings here come from the last time this site rebuilt.'
      );
      status.append(nswChip);
      if (result.errors.length) {
        const error = el('span', 'freshness inline-error');
        error.textContent = result.errors.join(' · ');
        status.append(error);
      }

      const stations = result.readings.length;
      detail.innerHTML = worst
        ? `Of ${number(stations)} stations reporting right now, the dirtiest is <strong>${worst.stationName}</strong> at ` +
          `<strong>${concentration(worst.pm25)} ${UNIT}</strong> — ${bandLabel(worst.pm25)}.` +
          (worst.pm25 !== null && worst.pm25 <= bundle.meta.smoke1.absoluteFloorUgM3
            ? ' Nothing is happening anywhere in the live network, which is the ordinary state of Australian air.'
            : '') +
          ` ${glossaryLink('reference monitor')}s and low-cost smoke sensors are labelled separately in the tooltips.`
        : 'No live reading came back from either feed. This is usually a temporary outage at the source rather than a fault here.';

      if (result.readings.length) {
        const bounds = L.latLngBounds(result.readings.map((reading) => [reading.lat, reading.lon] as [number, number]));
        fitWhenLaid(map, canvas, bounds);
      } else {
        fitWhenLaid(map, canvas, L.latLngBounds(bundle.stations.map((s) => [s.lat, s.lon] as [number, number])));
      }
    } catch {
      status.innerHTML = '';
      const chip = el('span', 'freshness inline-error');
      chip.textContent = 'The live feeds could not be reached';
      status.append(chip);
      detail.textContent =
        'Queensland and the ACT publish these feeds directly to browsers, so a failure here is normally at the source. The historical record on every other view is unaffected.';
    }
  }

  void refresh();
  // Polling at a respectful interval: both sources publish hourly, so five minutes is already
  // more often than the data can change.
  const timer = window.setInterval(() => void refresh(), 5 * 60_000);
  const observer = new MutationObserver(() => {
    if (!document.body.contains(canvas)) {
      clearInterval(timer);
      controller.abort();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  container.append(
    legend([
      ...BANDS.map((band) => ({ label: band.label, hex: band.hex })),
      { label: 'no live feed (NSW)', hex: 'var(--missing)' },
    ])
  );
}

// ── View ────────────────────────────────────────────────────────────────────

export async function renderMap({ bundle, container }: { bundle: Bundle; container: HTMLElement }): Promise<void> {
  const state = getState();
  const mode = (localStorage.getItem('air-episodes.mapMode') as 'replay' | 'live') ?? 'replay';

  const episode =
    bundle.episodes.find((candidate) => candidate.id === state.episode) ?? bundle.episodes[0];

  const view = panel({
    title: mode === 'live' ? 'The air right now' : 'Replay an episode',
    subtitle:
      mode === 'live'
        ? 'Queensland and the ACT publish their monitoring feeds in a form a browser can read directly, so this map is fetched live in your browser with nothing in between. New South Wales does not, and says so on the map.'
        : 'Every cluster that saw this episode, hour by hour, with the wind measured on the same mast in the same hour. Circle area follows concentration; colour follows the published category scale.',
  });

  const controls = el('div', 'control-row');
  const modes = el('div', 'segmented');
  for (const [value, label] of [
    ['replay', 'Replay'],
    ['live', 'Live'],
  ] as const) {
    const button = el('button', `segment${value === mode ? ' segment-on' : ''}`);
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', () => {
      localStorage.setItem('air-episodes.mapMode', value);
      setState({ view: 'map' });
      // setState is a no-op when nothing changed, so re-render explicitly.
      container.innerHTML = '';
      void renderMap({ bundle, container });
    });
    modes.append(button);
  }
  controls.append(modes);

  if (mode === 'replay') {
    const options = bundle.episodes
      .slice()
      .sort((a, b) => a.rankReach - b.rankReach)
      .slice(0, 200)
      .map((candidate) => ({
        value: candidate.id,
        label: `${prettyHour(candidate.peakLocal)} — ${candidate.peakClusterName} (${concentration(candidate.peakValue)} ${UNIT})`,
      }));
    controls.append(select('Episode', options, episode?.id ?? '', (value) => setState({ episode: value })));
  }
  view.body.append(controls);
  container.append(view.root);

  if (mode === 'live') {
    renderLive(view.body, bundle);
    return;
  }

  if (!episode) {
    view.body.append(emptyState('No episodes have been detected.', 'That would mean no hour in the record exceeded the daily standard, which would itself be remarkable.'));
    return;
  }

  const slice = await loadEpisodeSlice(episode.id);
  if (!slice) {
    view.body.append(
      emptyState(
        'This episode has no hourly detail file.',
        'Hourly slices are published for the 200 episodes with the widest reach. Choose one of those to replay it.'
      )
    );
    return;
  }

  renderReplay(view.body, bundle, episode, slice);

  view.body.append(
    el(
      'p',
      'note',
      `Peak ${concentration(episode.peakValue)} ${UNIT} at ${episode.peakClusterName}, ${prettyHour(
        episode.peakLocal
      )}. ${episode.clusterCount} clusters affected across ${episode.spanKm} km over ${episode.durationHours} hours.` +
        (episode.frontKmH !== null && episode.frontR2 !== null
          ? ` Fitting each cluster's distance from the first one against how long it took to arrive gives a front speed of ${episode.frontKmH} km/h with an R² of ${episode.frontR2} — the R² is shown because a low one means the arrivals had no travelling structure at all, which is itself the answer to "did it travel or appear everywhere at once".`
          : ' Too few clusters saw this one to estimate a front speed.') +
        (episode.suspect ? ' This episode was seen by only one cluster and is marked suspect.' : '')
    )
  );

  const next = el('div', 'control-row');
  const toMatrix = el('button', 'button');
  toMatrix.type = 'button';
  toMatrix.textContent = 'See how it propagated';
  toMatrix.addEventListener('click', () => setState({ view: 'propagation', episode: episode.id }));
  next.append(toMatrix);
  const toLedger = el('button', 'button');
  toLedger.type = 'button';
  toLedger.textContent = 'Back to the ledger';
  toLedger.addEventListener('click', () => setState({ view: 'ledger' }));
  next.append(toLedger);
  view.body.append(next);
}
